import { Head } from "@inertiajs/react"

import ContentPageLayout from "@/layouts/content-page-layout"

export default function Terms() {
  return (
    <ContentPageLayout>
      <Head title="Terms of Service" />

      <article className="prose-invert prose prose-lg max-w-none">
        <h1 className="font-display mb-2 text-4xl tracking-wide md:text-5xl">
          TERMS OF SERVICE
        </h1>
        <p className="text-bone-muted mb-12 text-sm">
          Last updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </p>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">THE DEAL</h2>
          <p className="text-bone-muted">
            ToDoOrDie is a to-do app with a twist: if you don&apos;t complete
            your tasks, they die. By using this service, you agree that
            we&apos;re not responsible for any emotional distress caused by
            watching your tasks flatline into the graveyard. That&apos;s kind of
            the point.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">THE SERVICE</h2>
          <p className="text-bone-muted mb-4">
            We provide a task management application that automatically expires
            (murders) tasks that aren&apos;t completed by their due date.
            Features include:
          </p>
          <ul className="text-bone-muted list-inside list-disc space-y-2">
            <li>Creating and managing tasks with deadlines</li>
            <li>Watching your tasks slowly approach their demise</li>
            <li>A graveyard of your failed ambitions</li>
            <li>The occasional existential crisis about productivity</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">YOUR ACCOUNT</h2>
          <p className="text-bone-muted mb-4">
            You&apos;re responsible for keeping your account secure. Don&apos;t
            share your login credentials. Don&apos;t let your cat walk across
            the keyboard and accidentally delete all your tasks. (Though
            honestly, the cat might be onto something.)
          </p>
          <p className="text-bone-muted">
            You must be at least 13 years old to use ToDoOrDie. If you&apos;re
            younger, go outside and play. Your to-do list can wait.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">
            ACCEPTABLE USE
          </h2>
          <p className="text-bone-muted mb-4">Don&apos;t use ToDoOrDie to:</p>
          <ul className="text-bone-muted list-inside list-disc space-y-2">
            <li>Break any laws (obviously)</li>
            <li>Harass, abuse, or harm others</li>
            <li>Attempt to hack, exploit, or break our systems</li>
            <li>
              Create tasks that are illegal, harmful, or just really gross
            </li>
            <li>
              Impersonate someone else (be yourself, even if that self has 47
              overdue tasks)
            </li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">
            THE PRICE (IT&apos;S FREE)
          </h2>
          <p className="text-bone-muted">
            ToDoOrDie is currently free to use. No subscription fees. No hidden
            charges. No &quot;premium features&quot; locked behind a paywall. We
            reserve the right to introduce paid features in the future, but
            we&apos;ll never take away what you already have for free. If the
            app ever costs money, we&apos;ll let you know in advance.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">YOUR CONTENT</h2>
          <p className="text-bone-muted mb-4">
            Your tasks and lists belong to you. We don&apos;t claim ownership of
            your content. We just store it so you can access it. When you delete
            something, it&apos;s gone. When a task dies, it goes to your
            graveyard (but it&apos;s still yours).
          </p>
          <p className="text-bone-muted">
            Please don&apos;t store anything illegal, harmful, or genuinely
            disturbing in your tasks. We really don&apos;t want to know about
            it.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">NO WARRANTIES</h2>
          <p className="text-bone-muted">
            ToDoOrDie is provided &quot;as is.&quot; We do our best to keep
            things running smoothly, but we can&apos;t guarantee the service
            will be available 100% of the time. Servers crash. Bugs happen.
            Sometimes the internet just decides to take a day off. We&apos;re
            not liable for any damages resulting from service interruptions,
            data loss, or the psychological impact of seeing all your tasks die
            at once.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">
            LIMITATION OF LIABILITY
          </h2>
          <p className="text-bone-muted">
            To the maximum extent permitted by law, ToDoOrDie and its creators
            shall not be liable for any indirect, incidental, special, or
            consequential damages. This includes, but is not limited to: lost
            productivity, missed deadlines, forgotten anniversaries, or the
            shame of having to explain why you never &quot;organized the
            garage.&quot;
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">TERMINATION</h2>
          <p className="text-bone-muted mb-4">
            You can delete your account at any time from your settings. We can
            also terminate your account if you violate these terms or use the
            service in ways that could harm us or other users.
          </p>
          <p className="text-bone-muted">
            When your account is terminated, your data is deleted. All of it.
            It&apos;s like you were never here. Poetic, really.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">
            CHANGES TO THESE TERMS
          </h2>
          <p className="text-bone-muted">
            We may update these terms from time to time. When we do, we&apos;ll
            post the new version here with an updated date. Continued use of
            ToDoOrDie after changes are posted constitutes acceptance of those
            changes. (We take deadlines seriously. You should too.)
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-2xl tracking-wide">GOVERNING LAW</h2>
          <p className="text-bone-muted">
            These terms are governed by the laws of wherever we happen to
            incorporate. Any disputes shall be resolved through binding
            arbitration, interpretive dance, or a really intense staring
            contest. (Just kidding. Mostly arbitration.)
          </p>
        </section>

        <section className="border-border mt-16 border-t pt-8">
          <p className="text-bone-dim text-sm">
            Questions about these terms? Contact us at{" "}
            <a
              href="mailto:hello@todoordie.app"
              className="text-red hover:text-red-bright transition-colors"
            >
              hello@todoordie.app
            </a>
            . Our legal team (it&apos;s just us in a wig) will get back to you.
          </p>
        </section>
      </article>
    </ContentPageLayout>
  )
}
